import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, Users, Target, Zap, Linkedin, ExternalLink, Menu, X, ChevronDown, ChevronUp, Grid, Smartphone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ProjectCarousel from '../components/ProjectCarousel';
import SimplePDFViewer from '../components/SimplePDFViewer';
import MobileProjectSwiper from '../components/MobileProjectSwiper';

const Projects = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showTableOfContents, setShowTableOfContents] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [viewMode, setViewMode] = useState('swiper'); // 'grid' or 'swiper' - default to swiper
  const [swiperProjectIndex, setSwiperProjectIndex] = useState(0);

  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // Remove auto-switch logic - let users choose their preferred view mode
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleOpenFullPdf = (projectId) => {
    navigate(`/projects/${projectId}/view`);
  };

  const pdfProjects = [
    {
      title: 'Customer-Centric Loyalty & Retention',
      company: 'Automotive OEM',
      description: 'Transformation Assessment: Combine currently fragmented customer touchpoints into a unified Global Umbrella Offering, One Auto Shop & Buy | Ownership | Charging | Care & Service',
      challenge: 'Fragmented customer touchpoints across multiple systems and departments.',
      approach: 'Comprehensive transformation assessment to unify customer experience.',
      outcome: 'Unified customer journey with improved retention and satisfaction.',
      technologies: ['Customer Experience', 'Digital Transformation', 'Loyalty Programs', 'Process Design'],
      pdfPath: '/assets/projects/Customer-Centric Loyalty & Retention.pdf',
      projectId: 'customer-centric-loyalty',
      uiStyle: 'thumbnail',
      category: 'Experience Design'
    },
    {
      title: 'E2E Process Assessment',
      company: 'Content Management Platform',
      description: 'Design Thinking Assessment to effectively scale a Customer Program',
      challenge: 'Inefficient content management processes limiting scalability.',
      approach: 'Design thinking methodology to assess and redesign end-to-end processes.',
      outcome: 'Streamlined processes enabling effective program scaling.',
      technologies: ['Process Design', 'Design Thinking', 'Content Management', 'Scalability'],
      pdfPath: '/assets/projects/E2E Content Management Assessment.pdf',
      projectId: 'e2e-process-assessment',
      uiStyle: 'thumbnail',
      category: 'Process Excellence'
    },
    {
      title: 'Freight Electrification Analysis',
      company: 'Logistics & Transportation',
      description: 'Electrification Assessment on how a client can effectively electrify their Freight Supply Chain to decarbonize emissions, lower costs, and streamline operations',
      challenge: 'High emissions and operational costs in freight supply chain.',
      approach: 'Comprehensive electrification assessment and implementation strategy.',
      outcome: 'Decarbonized operations with reduced costs and improved efficiency.',
      technologies: ['Electrification', 'Supply Chain', 'Sustainability', 'Cost Optimization'],
      pdfPath: '/assets/projects/ElectroMarket Freight Analysis_7.19.2023.pdf',
      projectId: 'freight-electrification',
      uiStyle: 'thumbnail',
      category: 'Analytics & Strategy'
    },
    {
      title: 'Apple CarPlay CX Review',
      company: 'Automotive UX',
      description: 'Analyzing the User Experience of CarPlay\'s Home Screen using Apple\'s CarPlay Developer Guidelines',
      challenge: 'Complex user interface affecting driver safety and experience.',
      approach: 'Comprehensive UX analysis using Apple\'s design guidelines.',
      outcome: 'Improved user experience with enhanced safety and usability.',
      technologies: ['User Experience', 'CarPlay', 'Design Guidelines', 'Automotive UX'],
      pdfPath: '/assets/projects/Design Review - Apple CarPlay Homescreen in Sensus.pdf',
      projectId: 'apple-carplay-cx',
      uiStyle: 'thumbnail',
      category: 'Experience Design'
    },
    {
      title: 'GTM Strategy: Generative AI Product',
      company: 'AI Technology',
      description: 'Defining the Go-to-Market Strategy for a new Generative AI product for Sales & Marketing: Sales Co-Pilot',
      challenge: 'New AI product requiring effective market entry strategy.',
      approach: 'Comprehensive GTM strategy development for AI-powered sales solution.',
      outcome: 'Successful market entry with clear value proposition and adoption strategy.',
      technologies: ['AI/ML', 'GTM Strategy', 'Sales Technology', 'Product Strategy'],
      pdfPath: '/assets/projects/C3 Generative AI - Sales Co-Pilot.pdf',
      projectId: 'gtm-strategy-ai',
      uiStyle: 'thumbnail',
      category: 'Business Development'
    },
    {
      title: 'Uber Global Strategy Analytics Case',
      company: 'Ride-Sharing Platform',
      description: 'Strategic analytics and business case development for Uber\'s global expansion',
      challenge: 'Complex global expansion requiring data-driven strategic decisions.',
      approach: 'Comprehensive analytics framework for global market entry.',
      outcome: 'Data-driven expansion strategy with clear market prioritization.',
      technologies: ['Analytics', 'Global Strategy', 'Market Analysis', 'Business Intelligence'],
      pdfPath: '/assets/projects/Uber Global Strategy Analytics Case v2.pdf',
      projectId: 'uber-global-strategy',
      uiStyle: 'thumbnail',
      category: 'Analytics & Strategy'
    },
    {
      title: 'Research Enablement',
      company: 'AI-Aware Scale-Up',
      description: 'AI-Aware Scale-Up Final Report: Comprehensive analysis of AI integration strategies for scaling businesses.',
      externalLink: "https://www.drivesweden.net/sites/default/files/2024-02/ai-aware-scale-up-final-report-v1.0-1.pdf",
      imagePath: "/assets/projects/Research Enablement.png",
      technologies: ["AI Strategy", "Scale-Up Analysis", "Research Methodology"],
      category: 'Strategic Leadership'
    }
  ];

  const legacyProjects = [
    {
      title: 'AI Strategy Transformation',
      company: 'Fortune 500 Automotive',
      description: 'Led comprehensive AI governance and strategy implementation across business units.',
      challenge: 'Siloed AI deployments causing inefficiencies.',
      approach: 'Established cross-functional AI governance framework.',
      outcome: '40% efficiency gains; $50M annual savings.',
      technologies: ['AI/ML', 'Strategy', 'Governance', 'Change Management'],
      category: 'Strategic Leadership'
    },
    {
      title: 'Business Development',
      company: 'CloudTrucks',
      description: 'Built and managed strategic partnerships driving 300% revenue growth.',
      challenge: 'High acquisition costs limiting growth.',
      approach: 'Built strategic partnerships with complementary providers.',
      outcome: '$15M revenue increase; 60% CAC reduction.',
      technologies: ['Business Development', 'GTM Strategy', 'Partnerships', 'Sales'],
      category: 'Business Development'
    },
    {
      title: 'Product & Autonomous Strategy',
      company: 'Volvo Cars',
      description: 'Led autonomous vehicle commercialization strategy and product development.',
      challenge: 'Delayed AV commercialization due to fragmented tech.',
      approach: 'Developed cohesive AV product roadmap & structured strategic partnerships.',
      outcome: '40% faster go-to-market; industry-leading safety and regulatory alignment.',
      technologies: ['Product Strategy', 'Autonomous Vehicles', 'Regulatory Compliance', 'Partnerships'],
      category: 'Strategic Leadership'
    },
    {
      title: 'Digital Transformation',
      company: 'Accenture (Fortune 500 Clients)',
      description: 'Led enterprise-wide digital transformation for Fortune 500 clients.',
      challenge: 'Legacy systems limiting innovation.',
      approach: 'Led modernization and change-management initiatives.',
      outcome: '35% increase in customer satisfaction; 25% cost reduction.',
      technologies: ['Digital Transformation', 'Change Management', 'Technology Strategy', 'Customer Experience'],
      category: 'Strategic Leadership'
    },
    {
      title: 'Corporate & Product Strategy',
      company: 'Fortune 100 Automotive',
      description: 'Developed M&A and market entry strategies for high-growth startups.',
      challenge: 'Identify and capitalize on new revenue streams in autonomous and electric vehicle markets.',
      approach: 'Conducted detailed industry analyses, identified strategic growth opportunities, and developed innovative business models for autonomous, EV, and insurance offerings.',
      outcome: 'Secured executive sponsorship for strategic initiatives, directly shaping the company\'s investment in emerging technology markets.',
      technologies: ['M&A', 'Market Analysis', 'Strategy', 'Business Development'],
      category: 'Business Development'
    },
    {
      title: 'Operational Excellence Program',
      company: 'High-growth Tech',
      description: 'Led organizational transformation and operational efficiency initiatives.',
      challenge: 'Inefficient processes constraining scale.',
      approach: 'Implemented lean methodology & redesigned core processes.',
      outcome: '45% operational efficiency improvement; 30% cost savings.',
      technologies: ['Process Optimization', 'Change Management', 'Lean Methodology', 'Team Leadership'],
      category: 'Process Excellence'
    }
  ];

  const projects = [...pdfProjects, ...legacyProjects];

  const categories = ['All', 'Strategic Leadership', 'Business Development', 'Experience Design', 'Analytics & Strategy', 'Process Excellence'];

  const filteredProjects = selectedCategory === 'All' 
    ? projects 
    : projects.filter(project => project.category === selectedCategory);

  const scrollToProject = (projectTitle) => {
    // Find the project index in the filtered array for swiper navigation
    const projectIndex = filteredProjects.findIndex(p => p.title === projectTitle);
    
    // If in swiper mode on mobile, switch to the correct project and scroll to swiper
    if (isMobile && viewMode === 'swiper') {
      // Set the current project index in the swiper
      if (projectIndex !== -1) {
        setSwiperProjectIndex(projectIndex);
        // Scroll to the mobile controls
        setTimeout(() => {
          const mobileControls = document.querySelector('.flex.justify-center.items-center.gap-4.mt-6.mb-4');
          if (mobileControls) {
            const rect = mobileControls.getBoundingClientRect();
            const scrollTop = window.pageYOffset + rect.top - 0; // 0px offset - align with top
            window.scrollTo({ top: scrollTop, behavior: 'smooth' });
          }
        }, 100);
      }
    } else {
      // Desktop or grid view - scroll to card with larger offset
      setTimeout(() => {
        const element = document.getElementById(`project-${projectTitle.replace(/\s+/g, '-').toLowerCase()}`);
        if (element) {
          // Use larger offset to account for all padding/margins
          const rect = element.getBoundingClientRect();
          const scrollTop = window.pageYOffset + rect.top - 500; // 500px offset for header + padding
          window.scrollTo({ top: scrollTop, behavior: 'smooth' });
        }
      }, 50);
    }
    setShowTableOfContents(false);
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="section bg-gradient-to-br from-primary-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <h1 className="section-title">Strategic Initiatives</h1>
            <p className="section-subtitle">
              Transformative projects and strategic initiatives that demonstrate executive leadership and business impact
            </p>
            <p className="text-primary-700 text-lg leading-relaxed">
              These initiatives showcase my ability to lead complex organizational transformations, 
              drive strategic partnerships, and deliver measurable business outcomes through 
              innovative approaches and cross-functional collaboration.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Projects Content */}
      <section className="section bg-white pt-0">
        <div className="max-w-6xl mx-auto">
          {/* Scroll Indicator - Moved above Project Examples header */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="flex justify-center items-center py-1 mb-8"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-6 h-10 border-2 border-primary-400 rounded-full flex justify-center"
            >
              <motion.div
                animate={{ y: [0, 12, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="w-1 h-3 bg-primary-400 rounded-full mt-2"
              />
            </motion.div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-1"
          >
            <h2 className="section-title">Project Examples</h2>
            {isMobile && (
              <p className="text-primary-600 text-sm mt-2">
                {viewMode === 'swiper' ? 'Swipe to browse projects' : 'Tap on any project to view details'}
              </p>
            )}
          </motion.div>
          
          {/* Mobile Controls - Moved below Project Examples title */}
          {isMobile && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="flex justify-center items-center gap-4 mt-6 mb-4"
            >
              {/* View Mode Toggle */}
              <div className="flex bg-primary-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('swiper')}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                    viewMode === 'swiper' 
                      ? 'bg-white text-primary-800 shadow-sm' 
                      : 'text-primary-600 hover:text-primary-800'
                  }`}
                >
                  <Smartphone size={16} />
                  Swipe
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                    viewMode === 'grid' 
                      ? 'bg-white text-primary-800 shadow-sm' 
                      : 'text-primary-600 hover:text-primary-800'
                  }`}
                >
                  <Grid size={16} />
                  Grid
                </button>
              </div>

              {/* Table of Contents Toggle */}
              <button
                onClick={() => setShowTableOfContents(!showTableOfContents)}
                className="flex items-center gap-2 bg-primary-100 text-primary-800 px-4 py-2 rounded-lg hover:bg-primary-200 transition-colors"
              >
                {showTableOfContents ? <X size={16} /> : <Menu size={16} />}
                {showTableOfContents ? 'Hide' : 'Contents'}
              </button>
            </motion.div>
          )}
          
          {/* Desktop Table of Contents Toggle */}
          {!isMobile && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="flex justify-center mt-6 mb-4"
            >
              <button
                onClick={() => setShowTableOfContents(!showTableOfContents)}
                className="flex items-center gap-2 bg-primary-100 text-primary-800 px-6 py-3 rounded-lg hover:bg-primary-200 transition-colors"
              >
                {showTableOfContents ? <X size={18} /> : <Menu size={18} />}
                {showTableOfContents ? 'Hide Table of Contents' : 'Show Table of Contents'}
              </button>
            </motion.div>
          )}
          
          {/* Table of Contents (Mobile & Desktop) */}
          <AnimatePresence>
            {showTableOfContents && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="mt-4 max-w-4xl mx-auto"
              >
                <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 border border-primary-200">
                  <h3 className="text-lg sm:text-xl font-semibold text-primary-900 mb-4">Project Contents</h3>
                  
                  {/* Category Filter */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-primary-700 mb-2">Filter by Category:</label>
                    <div className="flex flex-wrap gap-2">
                      {categories.map((category) => (
                        <button
                          key={category}
                          onClick={() => setSelectedCategory(category)}
                          className={`px-3 py-1 rounded-lg text-sm font-medium transition-all duration-200 ${
                            selectedCategory === category
                              ? 'bg-primary-800 text-white shadow-sm'
                              : 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                          }`}
                        >
                          {category}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Project List */}
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {filteredProjects.map((project, index) => {
                      // Find the original index in the full projects array
                      const originalIndex = projects.findIndex(p => p.title === project.title);
                      return (
                        <button
                          key={index}
                          onClick={() => scrollToProject(project.title)}
                          className="block w-full text-left p-3 hover:bg-primary-50 rounded-lg text-sm text-primary-700 hover:text-primary-900 transition-colors border border-transparent hover:border-primary-200"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <span className="font-medium text-primary-900">{project.title}</span>
                              <span className="block text-xs text-primary-500 mt-1">{project.company}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {project.category && (
                                <span className="px-2 py-1 bg-primary-100 text-primary-800 text-xs font-medium rounded-full">
                                  {project.category}
                                </span>
                              )}
                              <span className="text-xs text-primary-400">#{originalIndex + 1}</span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  
                  <div className="mt-4 text-xs text-primary-500 text-center">
                    Click any project to jump to it • {filteredProjects.length} projects shown
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Mobile Swiper View */}
          {isMobile && viewMode === 'swiper' && (
            <div className="mb-12">
              <div className="w-full mb-6">
                <MobileProjectSwiper 
                  projects={filteredProjects}
                  onOpenFullPdf={handleOpenFullPdf}
                  initialIndex={swiperProjectIndex}
                />
              </div>
              {/* Helper text moved here - between swiper and CTA buttons */}
              <div className="text-center mb-8">
                <p className="text-xs text-primary-500">
                  Swipe left/right or use arrows to navigate
                </p>
              </div>
            </div>
          )}
          
          {/* Grid View (Desktop or Mobile Grid Mode) */}
          {(!isMobile || viewMode === 'grid') && (
            <div className="mb-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-start">
                {filteredProjects.map((project, index) => (
                  <motion.div
                    key={index}
                    id={`project-${project.title.replace(/\s+/g, '-').toLowerCase()}`}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="card group hover:shadow-xl transition-all duration-300 flex flex-col h-fit w-full overflow-hidden"
                  >
                  {/* PDF Carousel or External Link */}
                  {project.pdfPath ? (
                    <div className="w-full flex-shrink-0">
                      <SimplePDFViewer
                        pdfPath={project.pdfPath}
                        onOpenFullPdf={() => handleOpenFullPdf(project.projectId)}
                      />
                    </div>
                  ) : project.externalLink ? (
                    <div className="w-full flex-shrink-0">
                      <div className="relative bg-white rounded-lg overflow-hidden shadow-lg">
                        {project.imagePath ? (
                          <div className="w-full flex items-center justify-center p-4 min-h-[300px] sm:min-h-[400px]">
                            <img 
                              src={project.imagePath} 
                              alt={project.title}
                              className="w-full h-auto object-contain max-w-full"
                            />
                          </div>
                        ) : (
                          <div className="aspect-video bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center w-full min-h-[300px] sm:min-h-[400px]">
                            <div className="text-primary-600 text-2xl sm:text-4xl font-bold">
                              {project.title.split(' ').map(word => word[0]).join('')}
                            </div>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => window.open(project.externalLink, '_blank')}
                        className="mt-4 w-full btn-secondary flex items-center justify-center gap-2"
                      >
                        <ExternalLink size={16} />
                        View Report
                      </button>
                    </div>
                  ) : null}
                  
                  {/* Legacy project card style */}
                  {!project.pdfPath && !project.externalLink && (
                    <div className="w-full flex-shrink-0">
                      <div className="aspect-video bg-gradient-to-br from-primary-100 to-primary-200 rounded-t-xl flex items-center justify-center w-full min-h-[300px] sm:min-h-[400px]">
                        <div className="text-primary-600 text-2xl sm:text-4xl font-bold">
                          {project.title.split(' ').map(word => word[0]).join('')}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="p-4 sm:p-6 flex-shrink-0">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                      <h3 className="text-lg sm:text-xl font-semibold text-primary-900 group-hover:text-primary-700 transition-colors">
                        {project.title}
                      </h3>
                      <span className="text-sm text-primary-600 font-medium">
                        {project.company}
                      </span>
                    </div>
                    
                    {/* Category Tag */}
                    {project.category && (
                      <div className="mb-3">
                        <span className="inline-block px-3 py-1 bg-primary-800 text-white text-xs font-semibold rounded-full">
                          {project.category}
                        </span>
                      </div>
                    )}
                    
                    <p className="text-primary-700 mb-4 text-sm sm:text-base">
                      {project.description}
                    </p>
                    
                    <div className="space-y-3 mb-4">
                      {project.challenge && (
                        <div>
                          <span className="text-sm font-semibold text-primary-900">Challenge:</span>
                          <p className="text-sm text-primary-700">{project.challenge}</p>
                        </div>
                      )}
                      {project.approach && (
                        <div>
                          <span className="text-sm font-semibold text-primary-900">Action:</span>
                          <p className="text-sm text-primary-700">{project.approach}</p>
                        </div>
                      )}
                      {project.outcome && (
                        <div>
                          <span className="text-sm font-semibold text-primary-900">Impact:</span>
                          <p className="text-sm text-primary-700 font-medium">{project.outcome}</p>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      {project.technologies && project.technologies.map((tech, techIndex) => (
                        <span
                          key={techIndex}
                          className="px-2 sm:px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-xs font-medium"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="section bg-primary-50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <h2 className="section-title">Ready to Transform Your Organization?</h2>
            <p className="section-subtitle">
              Let's discuss how strategic leadership can drive your next phase of growth
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="btn-primary"
              >
                Start a Conversation
              </a>
              <a
                href="https://www.linkedin.com/in/neilsinghmamak/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
              >
                Connect on LinkedIn
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Projects; 